import { merge } from 'lodash-es';
import { subscribeToUserActivity } from './activity';
import type { AnalyticsSessionIdStore } from './session_id_store';
import { CookieAnalyticsSessionIdStore } from './session_id_store';
import type { Options } from './types';

/**
 * Create an analytics session id getter. Structured to make testing easier.
 * Why not a class you ask? This seems neater.
 *
 * @param options Store and keepalive implementations
 * @returns analytics session id getter
 */
export const createGetAnalyticsSessionId = (options: {
  store: AnalyticsSessionIdStore;
  subscribeToUserActivity: typeof subscribeToUserActivity;
}) => {
  // Keep session id alive when user is active.
  options.subscribeToUserActivity(() => {
    options.store.refreshAnalyticsSession();
  });
  return (): string => {
    let sessionId = options.store.retrieveAnalyticsSessionId();
    if (sessionId == null) {
      // Session expired, create a new session

      // Session id format: milliseconds unix time
      // https://developers.amplitude.com/docs/http-api-v2#section-properties
      sessionId = String(+new Date());

      options.store.storeAnalyticsSessionId(sessionId);
    }

    return sessionId;
  };
};

const SESSION_COOKIE_NAME = 'timely_analytics_session';
const SESSION_EXPIRY_MINUTES = 30;
/**
 *
 * We're trying to mirror the default Amplitude analytics session behavior,
 * which is to expire the analytics session id after 30 minutes of inactivity.
 * Note that this is not an auth session,
 * but a kind of analytics attribute that ties together analytics events.
 * Without expiry, we could have analytics sessions spanning months.
 * Without keep alive, sessions would expire if the user is active but there
 * is no tracking events for 30 minutes.
 */
const getAnalyticsSessionId = createGetAnalyticsSessionId({
  store: new CookieAnalyticsSessionIdStore(SESSION_COOKIE_NAME, SESSION_EXPIRY_MINUTES),
  subscribeToUserActivity,
});

/**
 * Returns the current analytics session id as a number, matching Amplitude's
 * HTTP V2 `session_id` format (milliseconds since epoch). Used when the session
 * id needs to be sent across the wire (e.g. propagated to the AI service so
 * backend-emitted Amplitude events join the user's session — see TT-5024).
 */
export function getAnalyticsSessionIdAsNumber(): number {
  return Number(getAnalyticsSessionId());
}

/**
 * Adds analytics session id to a segment option object.
 */
export function addSessionId(options?: Options): Options {
  return {
    ...options,
    integrations: merge(options?.integrations ?? {}, {
      // https://segment.com/docs/connections/destinations/catalog/amplitude/#sessionid
      Amplitude: {
        session_id: getAnalyticsSessionId(),
      },
    }),
  };
}
